/**
 * Date Helper - Wrapper para date-fns
 * Facilita a migração de moment.js para date-fns
 * Fornece funções utilitárias para manipulação de datas
 */

import {
    format,
    parse,
    parseISO,
    isValid,
    addDays,
    addMonths,
    addYears,
    addMinutes,
    subDays,
    subMonths,
    subYears,
    startOfDay,
    endOfDay,
    startOfWeek,
    endOfWeek,
    startOfMonth,
    endOfMonth,
    isBefore,
    isAfter,
    isSameDay,
    differenceInDays,
    differenceInHours,
    differenceInMinutes,
    differenceInMonths,
    setDate,
    getDay,
} from 'date-fns';
import {ptBR, enUS, es, it} from 'date-fns/locale';

// Mapa de locales
const locales = {
    pt_BR: ptBR,
    en_US: enUS,
    es_ES: es,
    it_IT: it,
};

/**
 * Formata uma data
 * @param {Date|string|number} date - Data para formatar
 * @param {string} formatStr - Formato desejado (ex: 'dd/MM/yyyy', 'HH:mm')
 * @param {string} locale - Locale (pt_BR, en_US, etc)
 * @returns {string} Data formatada ou string vazia se inválida
 * 
 * @example
 * formatDate(new Date(), 'dd/MM/yyyy', 'pt_BR') // '06/12/2025'
 * formatDate('2025-12-06', 'dd/MM/yyyy HH:mm') // '06/12/2025 00:00'
 */
export const formatDate = (date, formatStr = 'dd/MM/yyyy', locale = 'pt_BR') => {
    if (!date) return '';

    const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);

    if (!isValid(dateObj)) return '';

    return format(dateObj, formatStr, {locale: locales[locale] || ptBR});
};

/**
 * Parse de string para Date
 * @param {string} dateString - String da data
 * @param {string} formatStr - Formato da string (padrão: 'dd/MM/yyyy')
 * @returns {Date|null} Objeto Date ou null se inválido
 */
export const parseDate = (dateString, formatStr = 'dd/MM/yyyy') => {
    if (!dateString) return null;
    return parse(dateString, formatStr, new Date());
};

/**
 * Parse de ISO string para Date
 * @param {string} isoString - String ISO (ex: '2025-12-06T10:30:00')
 * @returns {Date|null} Objeto Date ou null se inválido
 */
export const parseISODate = (isoString) => {
    if (!isoString) return null;
    return parseISO(isoString);
};

/**
 * Valida se é uma data válida
 * @param {Date|string|number} date - Data para validar
 * @returns {boolean} True se válida, false caso contrário
 */
export const isValidDate = (date) => {
    if (!date) return false;
    const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
    return isValid(dateObj);
};

/**
 * Adiciona dias a uma data
 * @param {Date|string|number} date - Data base
 * @param {number} days - Número de dias a adicionar
 * @returns {Date} Nova data
 */
export const addDaysToDate = (date, days) => {
    return addDays(new Date(date), days);
};

/**
 * Subtrai dias de uma data
 * @param {Date|string|number} date - Data base
 * @param {number} days - Número de dias a subtrair
 * @returns {Date} Nova data
 */
export const subtractDaysFromDate = (date, days) => {
    return subDays(new Date(date), days);
};

/**
 * Adiciona meses a uma data
 * @param {Date|string|number} date - Data base
 * @param {number} months - Número de meses a adicionar
 * @returns {Date} Nova data
 */
export const addMonthsToDate = (date, months) => {
    return addMonths(new Date(date), months);
};

/**
 * Subtrai meses de uma data
 * @param {Date|string|number} date - Data base
 * @param {number} months - Número de meses a subtrair
 * @returns {Date} Nova data
 */
export const subtractMonthsFromDate = (date, months) => {
    return subMonths(new Date(date), months);
};

/**
 * Adiciona anos a uma data
 * @param {Date|string|number} date - Data base
 * @param {number} years - Número de anos a adicionar
 * @returns {Date} Nova data
 */
export const addYearsToDate = (date, years) => {
    return addYears(new Date(date), years);
};

/**
 * Adiciona minutos a uma data
 * @param {Date|string|number} date - Data base
 * @param {number} minutes - Número de minutos a adicionar
 * @returns {Date} Nova data
 */
export const addMinutesToDate = (date, minutes) => {
    return addMinutes(new Date(date), minutes);
};

/**
 * Subtrai anos de uma data
 * @param {Date|string|number} date - Data base
 * @param {number} years - Número de anos a subtrair
 * @returns {Date} Nova data
 */
export const subtractYearsFromDate = (date, years) => {
    return subYears(new Date(date), years);
};

/**
 * Início do dia (00:00:00)
 * @param {Date|string|number} date - Data
 * @returns {Date} Data no início do dia
 */
export const getStartOfDay = (date) => {
    return startOfDay(new Date(date));
};

/**
 * Fim do dia (23:59:59)
 * @param {Date|string|number} date - Data
 * @returns {Date} Data no fim do dia
 */
export const getEndOfDay = (date) => {
    return endOfDay(new Date(date));
};

/**
 * Início da semana (Domingo)
 * @param {Date|string|number} date - Data
 * @returns {Date} Data no início da semana
 */
export const getStartOfWeek = (date) => {
    return startOfWeek(new Date(date), {weekStartsOn: 0}); // Domingo
};

/**
 * Fim da semana (Sábado)
 * @param {Date|string|number} date - Data
 * @returns {Date} Data no fim da semana
 */
export const getEndOfWeek = (date) => {
    return endOfWeek(new Date(date), {weekStartsOn: 0}); // Domingo
};

/**
 * Início do mês
 * @param {Date|string|number} date - Data
 * @returns {Date} Data no início do mês
 */
export const getStartOfMonth = (date) => {
    return startOfMonth(new Date(date));
};

/**
 * Fim do mês
 * @param {Date|string|number} date - Data
 * @returns {Date} Data no fim do mês
 */
export const getEndOfMonth = (date) => {
    return endOfMonth(new Date(date));
};

/**
 * Verifica se uma data é antes de outra
 * @param {Date|string|number} date1 - Primeira data
 * @param {Date|string|number} date2 - Segunda data
 * @returns {boolean} True se date1 é antes de date2
 */
export const isBeforeDate = (date1, date2) => {
    return isBefore(new Date(date1), new Date(date2));
};

/**
 * Verifica se uma data é depois de outra
 * @param {Date|string|number} date1 - Primeira data
 * @param {Date|string|number} date2 - Segunda data
 * @returns {boolean} True se date1 é depois de date2
 */
export const isAfterDate = (date1, date2) => {
    return isAfter(new Date(date1), new Date(date2));
};

/**
 * Verifica se duas datas são do mesmo dia
 * @param {Date|string|number} date1 - Primeira data
 * @param {Date|string|number} date2 - Segunda data
 * @returns {boolean} True se são do mesmo dia
 */
export const isSameDayDate = (date1, date2) => {
    return isSameDay(new Date(date1), new Date(date2));
};

/**
 * Diferença em dias entre duas datas
 * @param {Date|string|number} date1 - Primeira data
 * @param {Date|string|number} date2 - Segunda data
 * @returns {number} Número de dias de diferença
 */
export const getDifferenceInDays = (date1, date2) => {
    return differenceInDays(new Date(date1), new Date(date2));
};

/**
 * Diferença em horas entre duas datas
 * @param {Date|string|number} date1 - Primeira data
 * @param {Date|string|number} date2 - Segunda data
 * @returns {number} Número de horas de diferença
 */
export const getDifferenceInHours = (date1, date2) => {
    return differenceInHours(new Date(date1), new Date(date2));
};

/**
 * Diferença em minutos entre duas datas
 * @param {Date|string|number} date1 - Primeira data
 * @param {Date|string|number} date2 - Segunda data
 * @returns {number} Número de minutos de diferença
 */
export const getDifferenceInMinutes = (date1, date2) => {
    return differenceInMinutes(new Date(date1), new Date(date2));
};

/**
 * Diferença em meses entre duas datas
 * @param {Date|string|number} date1 - Primeira data
 * @param {Date|string|number} date2 - Segunda data
 * @returns {number} Número de meses de diferença
 */
export const getDifferenceInMonths = (date1, date2) => {
    return differenceInMonths(new Date(date1), new Date(date2));
};

/**
 * Define o dia do mês para uma data
 * @param {Date|string|number} date - Data base
 * @param {number} day - Dia do mês a definir
 * @returns {Date} Nova data com o dia definido
 */
export const setDateDay = (date, day) => {
    return setDate(new Date(date), day);
};

/**
 * Obtém o dia da semana (0 = Domingo, 1 = Segunda, etc.)
 * @param {Date|string|number} date - Data
 * @returns {number} Dia da semana
 */
export const getWeekDay = (date) => {
    return getDay(new Date(date));
};

/**
 * Retorna a data atual
 * @returns {Date} Data e hora atuais
 */
export const now = () => {
    return new Date();
};

/**
 * Formatos comuns de data
 */
export const DATE_FORMATS = {
    SHORT_DATE: 'dd/MM/yyyy',
    LONG_DATE: "dd 'de' MMMM 'de' yyyy",
    SHORT_TIME: 'HH:mm',
    LONG_TIME: 'HH:mm:ss',
    DATETIME: 'dd/MM/yyyy HH:mm',
    DATETIME_LONG: 'dd/MM/yyyy HH:mm:ss',
    ISO: "yyyy-MM-dd'T'HH:mm:ss",
};

export default {
    format: formatDate,
    parse: parseDate,
    parseISO: parseISODate,
    isValid: isValidDate,
    addDays: addDaysToDate,
    subDays: subtractDaysFromDate,
    addMonths: addMonthsToDate,
    subMonths: subtractMonthsFromDate,
    addYears: addYearsToDate,
    subYears: subtractYearsFromDate,
    addMinutes: addMinutesToDate,
    startOfDay: getStartOfDay,
    endOfDay: getEndOfDay,
    startOfWeek: getStartOfWeek,
    endOfWeek: getEndOfWeek,
    startOfMonth: getStartOfMonth,
    endOfMonth: getEndOfMonth,
    isBefore: isBeforeDate,
    isAfter: isAfterDate,
    isSameDay: isSameDayDate,
    differenceInDays: getDifferenceInDays,
    differenceInHours: getDifferenceInHours,
    differenceInMinutes: getDifferenceInMinutes,
    differenceInMonths: getDifferenceInMonths,
    setDate: setDateDay,
    getWeekDay,
    now,
    DATE_FORMATS,
};
